import React, { useState } from 'react';
import { Instagram, Youtube, Twitter, Facebook, ArrowUpRight, Check } from 'lucide-react';

const cols = [
  {
    title: 'Product',
    links: ['How it works', 'Curriculum', 'Learning English', 'Technology']
  },
  {
    title: 'Parents',
    links: ['Child safety', 'Parental controls', 'FAQ', 'Support']
  },
  {
    title: 'Company',
    links: ['Who we are', 'Contact', 'Careers']
  },
  {
    title: 'Our core values',
    links: ['Screen-free', 'Built for children', 'Privacy-first', 'AI designed for kids']
  }
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | sent | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus('error');
        setMessage(data.detail?.[0]?.msg || 'Something went wrong. Please try again.');
        setTimeout(() => { setStatus('idle'); setMessage(''); }, 3200);
        return;
      }
      setStatus('sent');
      setMessage(data.message || "You're on the list!");
      setTimeout(() => { setStatus('idle'); setMessage(''); setEmail(''); }, 3200);
    } catch (err) {
      setStatus('error');
      setMessage('Network error. Please try again.');
      setTimeout(() => { setStatus('idle'); setMessage(''); }, 3200);
    }
  };

  const sent = status === 'sent';
  const loading = status === 'loading';
  const errored = status === 'error';

  return (
    <footer id="waitlist" className="relative w-full bg-[#684FF7] text-white scroll-mt-24">
      {/* Organic white "page" tongue lobing down into the purple footer */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 w-full h-[120px] sm:h-[180px] lg:h-[230px] pointer-events-none"
      >
        <svg
          viewBox="0 0 1440 220"
          preserveAspectRatio="none"
          className="w-full h-full block"
        >
          <path
            d="M 0 0 L 1440 0 L 1440 70 C 1330 130, 1185 195, 985 178 C 815 164, 745 50, 560 76 C 410 98, 295 200, 130 175 C 65 165, 30 110, 0 92 Z"
            fill="#ffffff"
          />
        </svg>
      </div>

      {/* Decorative blurred orbs, clipped to the footer bounds */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-10 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-10 w-[480px] h-[480px] rounded-full bg-[#9B82FF]/40 blur-3xl"></div>
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 pt-[140px] sm:pt-[200px] lg:pt-[260px] pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-4">
            <p
              className="text-white text-[16px] lg:text-[17px] leading-relaxed max-w-sm"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
            >
              Screen-free English acquisition for kids, powered by interactive books and child-safe AI.
            </p>
            <div className="mt-7">
              <p className="text-[12px] tracking-[0.18em] font-bold uppercase text-white/70">Follow us</p>
              <div className="mt-3 flex items-center gap-3">
                {[Instagram, Youtube, Twitter, Facebook].map((Icn, i) => (
                  <a key={i} href="#" className="w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-white hover:text-[#684FF7] text-white transition-colors">
                    <Icn className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {cols.map((c) => (
              <div key={c.title}>
                <p className="text-[13px] font-bold tracking-[0.18em] uppercase text-white/70">{c.title}</p>
                <ul className="mt-4 space-y-3">
                  {c.links.map((l) => (
                    <li key={l}>
                      <a href="#" className="group inline-flex items-center gap-1 text-[15px] text-white hover:text-white/80 transition-colors">
                        {l}
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter row — centered: logo on top, input below */}
        <div className="mt-16 lg:mt-20 flex flex-col items-center gap-8">
          {/* Mascot + logo image — centered */}
          <img
            src="/spiick_imgs/logo-mascotte.png"
            alt="Spiick"
            className="w-full max-w-[460px] h-auto select-none pointer-events-none"
          />

          {/* Email input pill — centered below */}
          <form
            onSubmit={submit}
            data-testid="waitlist-form"
            className="w-full max-w-[480px] bg-white rounded-full p-1.5 flex items-center gap-2 shadow-[0_18px_45px_-18px_rgba(0,0,0,0.35)]"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email..."
              required
              data-testid="waitlist-email-input"
              className="flex-1 h-12 px-5 rounded-full bg-transparent outline-none text-[15px] text-[#0c0a1a] placeholder:text-[#9a98a8] min-w-0"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="waitlist-submit-button"
              className={`h-12 px-6 rounded-full text-white text-[15px] transition-colors disabled:opacity-70 ${sent ? 'bg-[#3CC58A]' : errored ? 'bg-[#0c0a1a]' : 'bg-[#F24540] hover:bg-[#d93a35]'}`}
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
            >
              {sent ? (<span className="inline-flex items-center gap-2"><Check className="w-4 h-4" /> Sent</span>) : loading ? 'Sending...' : errored ? 'Retry' : 'Send'}
            </button>
          </form>

          {message && (
            <p
              data-testid="waitlist-status-message"
              className={`text-[14px] -mt-3 ${sent ? 'text-[#abf1b2]' : errored ? 'text-[#FFB4B1]' : 'text-white/85'}`}
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
            >
              {message}
            </p>
          )}
        </div>

        <div className="mt-12 pt-6 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[13px] text-white/70">
          <p>© {new Date().getFullYear()} Spiick. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="#" className="hover:text-white">Privacy policy</a>
            <a href="#" className="hover:text-white">Terms of service</a>
            <a href="#" className="hover:text-white">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
